import React, { useEffect, useMemo, useRef, useState } from 'react';
import Loading from '../feedback/Loading';
import toast from 'react-hot-toast';
import utils from '../../utils';
import { HiTrash } from 'react-icons/hi';
import adminServices from '../../services/AdminServices';
import { useAuth } from '../../context/UserContext';
import moment from 'moment';
import { FaCheck, FaX } from 'react-icons/fa6'
import vendorServices from '../../services/VendorSevices';
import ProductMedia from '../products/ProductMedia';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function ProductModal(props) {
    const { user, store } = useAuth()
    const [name, setName] = useState('');
    const [sku, setSku] = useState('');
    const [price, setPrice] = useState('');
    const [quantity, setQuantity] = useState('');
    const [manufactureYear, setManufactureYear] = useState('');
    const [condition, setCondition] = useState('New'); // Default
    const [warrantyInfo, setWarrantyInfo] = useState('');
    const [description, setDescription] = useState('');
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [productType, setProductType] = useState('');
    const [isFeatured, setIsFeatured] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [addedProduct, setAddedProduct] = useState()
    const action = props.action;
    const activeProduct = props.activeProduct;
    const brands = props.brands;
    const models = props.models;
    const fileInputRef = useRef(null);

    // Initialize form on edit
    useEffect(() => {
        if (action === 1 && activeProduct) {
            setName(activeProduct.name || '');
            setSku(activeProduct.sku || '');
            setPrice(activeProduct.price || '');
            setQuantity(activeProduct.quantity || '');
            setManufactureYear(activeProduct.manufacture_year || '');
            setCondition(activeProduct.condition || 'New');
            setWarrantyInfo(activeProduct.warranty_info || '');
            setDescription(activeProduct.description || '');
            setBrand(activeProduct.brand || '');
            setModel(activeProduct.model || '');
            setProductType(activeProduct.product_type || '');
            setIsFeatured(!!activeProduct.is_featured);
            setIsApproved(!!activeProduct.is_approved);
            setIsActive(!!activeProduct.is_active);
        } else {
            // reset for add
            clearData()
        }
    }, [action, activeProduct]);

    const tabs = useMemo(() => {
        const baseTabs = ["Info", "Media"];
        if (action === 1) {
            return [...baseTabs, "Delete"];
        }
        return baseTabs;
    }, [action]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && !file.type.startsWith('image/')) {
            toast.error('Please select a valid image file.');
            e.target.value = '';
            return;
        }
        setImage(file);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const closeBtn = document.getElementById('product-modal-close-btn');
        if (!closeBtn) return;

        if (!description) {
            toast.error('Please provide a description for the product.');
            return
        }

        setLoading(true);
        const formData = new FormData();

        formData.append('name', name.trim());
        formData.append('sku', sku.trim());
        formData.append('price', price);
        formData.append('quantity', quantity);
        manufactureYear && formData.append('manufacture_year', manufactureYear);
        formData.append('condition', condition);
        warrantyInfo && formData.append('warranty_info', warrantyInfo);
        description && formData.append('description', description);
        formData.append('brand', brand);
        formData.append('model', model);
        formData.append('product_type', productType);
        formData.append('is_featured', isFeatured);
        formData.append('is_approved', isApproved);
        formData.append('is_active', isActive);

        if (image) {
            formData.append('image', image);
        }

        if (action === 0) {
            if (user?.is_admin) {
                formData.append('owner', user?.id);
            }

        }

        if (action === 0 && user?.is_vendor && store?.id) {
            formData.append('store', store?.id);
        }

        let apiCall

        if (user?.is_admin) {
            action === 1
                ? apiCall = adminServices.updateProduct(activeProduct.id, formData)
                : apiCall = adminServices.createProduct(formData);
        }
        if (user?.is_vendor) {
            action === 1
                ? apiCall = vendorServices.updateStoreProduct(activeProduct.id, formData)
                : apiCall = vendorServices.createStoreProduct(formData);
        }

        if (action === 1) {
            if (activeProduct.price !== price) {
                formData.append('old_price', activeProduct.price)
            }
        }

        apiCall
            .then((res) => {
                toast.success(`Product ${action === 1 ? 'Updated' : 'Added'} Successfully`);
                setLoading(false);
                props.getProducts?.();
                if (action === 0) {
                    setAddedProduct(res.data)

                    setActiveTab(1)
                }
                setActiveTab(1)
            })
            .catch((err) => {
                console.error(err);
                toast.error(utils.toastError || 'Failed to save product.');
                setLoading(false);
            });
    };

    const handleDelete = () => {
        if (!activeProduct?.id) return;

        const closeBtn = document.getElementById('product-modal-close-btn');
        setDeleteLoading(true);

        let apiCall
        if (user?.is_admin) {
            apiCall = adminServices.deleteProduct(activeProduct.id)
        }
        if (user?.is_vendor) {
            apiCall = vendorServices.deleteStoreProduct(activeProduct.id)
        }
        apiCall.then(() => {
            toast.success('Product Deleted Successfully');
            setDeleteLoading(false);
            props.getProducts?.();
            closeBtn?.click();
        })
            .catch((err) => {
                console.error(err);
                toast.error(utils.toastError || 'Failed to delete product.');
                setDeleteLoading(false);
            });
    };

    function changeTab(idx) {
        if (action === 1) {
            setActiveTab(idx)
        }
    }

    function updateUI() {
        const closeBtn = document.getElementById('product-modal-close-btn');
        props.getProducts?.();
        closeBtn.click()
    }

    function clearData() {
        setName('');
        setSku('');
        setPrice('');
        setQuantity('');
        setManufactureYear('');
        setCondition('New');
        setWarrantyInfo('');
        setDescription('');
        setBrand('');
        setModel('');
        setProductType('');
        setIsFeatured(false);
        setIsApproved(true);
        setIsActive(true);
        setImage(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    return (
        <div>
            <label htmlFor="product-modal" id="product-open-btn" className="hidden">
                Add / Update Product
            </label>
            <input type="checkbox" id="product-modal" className="modal-toggle" />
            <div className="modal">
                <div className="modal-box w-11/12 max-w-5xl">
                    <label
                        htmlFor="product-modal"
                        id="product-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                        onClick={() => {
                            setAddedProduct()
                            setActiveTab(0)
                            // Cleanup side effects if needed
                            if (action === 0) {
                                clearData()
                            }
                        }}
                    >
                        ✕
                    </label>

                    <h2 className="text-lg font-bold mb-4">
                        {action === 1 ? 'Update' : 'Add'} Product {action === 1 && activeProduct?.name && (<span className='ml-2 text-gray-500'>{activeProduct?.name}</span>)}
                        {(action === 0 && addedProduct) && (<span className='ml-2 text-gray-500'>Media for: {addedProduct?.name}</span>)}
                    </h2>

                    <div className="flex justify-center mb-6">
                        <div className=" border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white rounded-lg' : ''}`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {activeTab === 0 && (
                        <div>
                            {action === 1 && activeProduct?.image && (
                                <div className={user?.is_admin ? "mb-4 grid grid-cols-1 gap-3 md:grid-cols-3" : "mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-2"}>
                                    <div className='flex justify-center'>
                                        <img
                                            src={activeProduct.image}
                                            alt={activeProduct.name}
                                            className="rounded-md max-h-48 object-contain"
                                        />
                                    </div>
                                    <div className='border p-3 rounded-lg shadow-md'>
                                        <p className="font-bold mb-1">Overview</p>
                                        <span className='flex gap-3'>
                                            Is Active:
                                            <div>
                                                {activeProduct?.is_active ? (
                                                    <FaCheck />
                                                ) : (
                                                    <FaX />
                                                )}
                                            </div>
                                        </span>
                                        <span className='flex gap-3'>
                                            Is Approved:
                                            <div>
                                                {activeProduct?.is_approved ? (
                                                    <FaCheck />
                                                ) : (
                                                    <FaX />
                                                )}
                                            </div>
                                        </span>
                                        <span className='flex gap-3'>
                                            Is Featured:
                                            <div>
                                                {activeProduct?.is_featured ? (
                                                    <FaCheck />
                                                ) : (
                                                    <FaX />
                                                )}
                                            </div>
                                        </span>
                                        <p>Quantity: <span className='font-bold ml-3 text-gray-600'><span className={activeProduct?.quantity < 5 ? 'text-error' : ''}>{activeProduct?.quantity}</span></span></p>
                                        <p>Total Purchases: <span className='font-bold ml-3 text-gray-600'>{activeProduct?.purchases || 0}</span></p>
                                        <p>Created On: <span className='font-bold ml-3 text-gray-600'>{moment(activeProduct?.creation_time).format("MMM Do YYYY")}</span></p>
                                    </div>
                                    {user?.is_admin && activeProduct?.store && (
                                        <div className='border p-3 rounded-lg shadow-md'>
                                            <p className="font-bold mb-1">Store Overview</p>
                                            <p>Name: <span className='font-bold ml-3 text-gray-600'>{activeProduct?.store?.name}</span></p>
                                            <span className='flex gap-3 items-center'>
                                                Is Active:
                                                <div>
                                                    {activeProduct?.store?.is_active ? (
                                                        <FaCheck />
                                                    ) : (
                                                        <FaX />
                                                    )}
                                                </div>
                                            </span>
                                            <span className='flex gap-3 items-center'>
                                                Is Verified:
                                                <div>
                                                    {activeProduct?.store?.is_verified ? (
                                                        <FaCheck />
                                                    ) : (
                                                        <FaX />
                                                    )}
                                                </div>
                                            </span>
                                            <p>Product Count: <span className='font-bold ml-3 text-gray-600'>{activeProduct?.store?.product_count || 0}</span></p>
                                            <p>Total Orders: <span className='font-bold ml-3 text-gray-600'>{activeProduct?.store?.total_orders || 0}</span></p>
                                        </div>
                                    )}
                                    {user?.is_admin && activeProduct?.owner && (
                                        <div className='border p-3 rounded-lg shadow-md'>
                                            <p className="font-bold mb-1">Owner Overview</p>
                                            <div className=''>
                                                <div className="flex items-center border-gray-200 gap-3 mb-4">
                                                    <div className="avatar placeholder">
                                                        <div className="bg-neutral text-neutral-content w-14 rounded-full">
                                                            <span className="text-3xl">{utils.makeProfileLetter(activeProduct?.owner)}</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        {activeProduct?.owner?.full_name && (
                                                            <h3 className="font-semibold text-gray-800">{activeProduct?.owner?.full_name}</h3>
                                                        )}
                                                    </div>
                                                </div>
                                                <p>Email: <span className='font-bold ml-3 text-gray-600'>{activeProduct?.owner?.email}</span></p>
                                                <p>Product Count: <span className='font-bold ml-3 text-gray-600'>{activeProduct?.owner?.product_count}</span></p>
                                                <p>Joined: <span className='font-bold ml-3 text-gray-600'>{moment(activeProduct?.owner?.creation_time).format("MMM Do YYYY")}</span></p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Row 1: Name, SKU, Price */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Name *</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            className="input input-bordered"
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Price (KES) *</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={price}
                                            onChange={(e) => setPrice(e.target.value)}
                                            required
                                            min="0"
                                            className="input input-bordered"
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">SKU {action === 0 && "(Optional)"}</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={sku}
                                            onChange={(e) => setSku(e.target.value)}
                                            className="input input-bordered"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Quantity *</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={quantity}
                                            onChange={(e) => setQuantity(e.target.value)}
                                            required
                                            min="0"
                                            className="input input-bordered"
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Manufacture Year</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={manufactureYear}
                                            onChange={(e) => setManufactureYear(e.target.value)}
                                            min="1900"
                                            max="2030"
                                            className="input input-bordered"
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Condition *</span>
                                        </label>
                                        <select
                                            value={condition}
                                            onChange={(e) => setCondition(e.target.value)}
                                            required
                                            className="select select-bordered"
                                        >
                                            <option value="New">New</option>
                                            <option value="Used">Used</option>
                                            <option value="Refurbished">Refurbished</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Category *</span>
                                        </label>
                                        <select
                                            value={productType}
                                            onChange={(e) => setProductType(e.target.value)}
                                            required
                                            className="select select-bordered"
                                        >
                                            <option value="">Select Category</option>
                                            {/* Replace with dynamic product types */}
                                            {props.productTypes?.map((pt) => (
                                                <option key={pt.id} value={pt.id}>
                                                    {pt.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Brand *</span>
                                        </label>
                                        <select
                                            value={brand}
                                            onChange={(e) => setBrand(e.target.value)}
                                            className="select select-bordered"
                                        >
                                            <option value="">Select Brand</option>
                                            {brands?.map((b) => (
                                                <option key={b.id} value={b.id}>
                                                    {b.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Model *</span>
                                        </label>
                                        <select
                                            value={model}
                                            onChange={(e) => setModel(e.target.value)}
                                            className="select select-bordered"
                                        >
                                            <option value="">Select Model</option>
                                            {models?.map((pt) => (
                                                <option key={pt.id} value={pt.id}>
                                                    {pt.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>


                                </div>

                                {/* Row 4: Toggle switches */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {user?.is_admin && (
                                        <>
                                            <div className="form-control flex items-center">
                                                <label className="cursor-pointer label">
                                                    <span className="label-text mr-2">Featured</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={isFeatured}
                                                        onChange={(e) => setIsFeatured(e.target.checked)}
                                                        className="toggle toggle-primary"
                                                    />
                                                </label>
                                            </div>
                                            <div className="form-control flex items-center">
                                                <label className="cursor-pointer label">
                                                    <span className="label-text mr-2">Approved</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={isApproved}
                                                        onChange={(e) => setIsApproved(e.target.checked)}
                                                        className="toggle toggle-success"
                                                    />
                                                </label>
                                            </div>
                                        </>
                                    )}
                                    <div className="form-control flex items-center">
                                        <label className="cursor-pointer label">
                                            <span className="label-text mr-2">Active</span>
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={(e) => setIsActive(e.target.checked)}
                                                className="toggle toggle-accent"
                                            />
                                        </label>
                                    </div>
                                </div>

                                {/* Image Upload */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">{action === 0 ? 'Image *' : 'Image (Optional)'}</span>
                                    </label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="file-input file-input-bordered w-full"
                                        required={action === 0}
                                    />
                                </div>

                                {/* Description, Warranty */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Description *</span>
                                    </label>
                                    <ReactQuill
                                        value={description}
                                        onChange={setDescription}
                                        theme="snow"
                                    />
                                </div>

                                <div className="form-control pt-1">
                                    <label className="label">
                                        <span className="label-text">Warranty Info</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={warrantyInfo}
                                        onChange={(e) => setWarrantyInfo(e.target.value)}
                                        className="input input-bordered"
                                    />
                                </div>


                                {/* Submit Button */}
                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full btn btn-primary"
                                    >
                                        {loading ? (
                                            <span>{action === 0 ? 'Adding...' : 'Updating...'}</span>
                                        ) : (
                                            <span>{action === 0 ? 'Add Product' : 'Update Product'}</span>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === 1 && (
                        <ProductMedia
                            action={action}
                            activeProduct={activeProduct}
                            addedProduct={addedProduct}
                            updateUI={updateUI}
                        />
                    )}

                    {activeTab === 2 && (
                        <div>
                            {/* Delete Section (Edit Mode Only) */}
                            {action === 1 && activeProduct && (
                                <div className=" border-base-200">
                                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden">
                                        <div className="p-6 md:p-8">
                                            <div className="border-l-4 border-error pl-4 py-2">
                                                <h3 className="font-bold text-error">Warning</h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                                    Deleting this product is irreversible and may affect orders/inventory.
                                                </p>
                                                <div className="pt-4">
                                                    {deleteLoading ? (
                                                        <Loading />
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleDelete}
                                                            className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                                        >
                                                            <HiTrash className="h-4 w-4" />
                                                            <span>Delete Product</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default ProductModal;